import { sha256Hex } from "../integration/local-ifc";
import { canonicalJson, canonicalJsonBytes } from "./d2-property-proposal-candidate";
import type { D2LocalEnrichmentProposal } from "./d2-local-enrichment-proposal";
import type { D2LocalClassificationProposal } from "./d2-local-classification-proposal";

/**
 * Why this module exists (see bim-hub-integration/reports/2026-08-11-d3-writeback-go-request.md):
 * kaz approved D3 write-back in principle, and BIM-Hub's existing chunk-upload-session endpoint
 * shape was read from source (`bim-hub/edge/src/lib.rs`) — a request roughly
 * `{ idempotency_key, base_revision, page_changes, manifest_json, manifest_sha256, payload_sha256,
 * payload_bytes }` pinning a `base_revision` for an all-or-nothing CAS commit that always creates a
 * new ModelRevision (a stale base is rejected with 409; nothing is ever partially written). What is
 * still unpinned is *how* that endpoint would accept Workbench's Identity token instead of
 * BIM-Hub's own session JWT, so this module stops short of the network boundary: it only builds the
 * deterministic payload, in the same shape, from the D2-C local proposals that already exist. No
 * `fetch`, no transport (mocked or real) is called anywhere in this file.
 *
 * "base_revision" is BIM-Hub's authority, not Workbench's: a local IFC fixture carries no
 * ModelRevision pin today, so the default is always the explicit `{ known: false }` case. A caller
 * that does have a real pin (e.g. a future D1-A read) may supply `{ known: true, ... }` instead —
 * this module never invents one.
 */

export const D3_COMMIT_PAYLOAD_VERSION = "workbench-commit-payload-candidate/1" as const;

export type D3BaseRevision =
  | { readonly known: false }
  | { readonly known: true; readonly projectId: string; readonly modelId: string; readonly revision: number };

export type D3ProposalKind = "local-enrichment" | "local-classification";

export type D3ProposalEntryInput =
  | { readonly kind: "local-enrichment"; readonly proposal: D2LocalEnrichmentProposal }
  | { readonly kind: "local-classification"; readonly proposal: D2LocalClassificationProposal };

export interface D3CommitPayloadSource {
  readonly datasetId: string;
  readonly byteLength: number;
  readonly sha256: string;
}

export interface D3CommitPayloadInput {
  readonly source: D3CommitPayloadSource;
  readonly baseRevision: D3BaseRevision;
  readonly proposals: readonly D3ProposalEntryInput[];
}

/**
 * Workbench-side preview accounting, not a claim about BIM-Hub's authoritative
 * `ModelRevisionDiffReceipt` (`added_count`/`modified_count`/`removed_count`). It exists so the
 * review UI can show a change-kind breakdown before anything is sent. Mapping rule, fixed for this
 * increment: `property-add` and a first-time classification (`before === null`) count as `added`;
 * `property-remove` counts as `removed` (the property record itself goes away, the occurrence does
 * not); every other operation (`property-replace`, `property-clear`, a classification change with a
 * prior value) counts as `modified`.
 */
export type D3ChangeKind = "added" | "modified" | "removed";

export interface D3ChangeSummary {
  readonly addedCount: number;
  readonly modifiedCount: number;
  readonly removedCount: number;
  readonly totalOperations: number;
}

export interface D3ManifestProposalSummary {
  readonly kind: D3ProposalKind;
  readonly contractVersion: string;
  readonly status: "local-empty" | "local-proposal";
  readonly selected: number;
  readonly proposed: number;
  readonly operationCount: number;
  readonly operationDigests: readonly string[];
}

export interface D3CommitManifest {
  readonly contractVersion: typeof D3_COMMIT_PAYLOAD_VERSION;
  readonly source: D3CommitPayloadSource;
  readonly baseRevision: D3BaseRevision;
  readonly proposalCount: number;
  readonly changeSummary: D3ChangeSummary;
  readonly proposals: readonly D3ManifestProposalSummary[];
}

export type D3CommitPayloadRefusalReason = "proposal-population-refused" | "writer-unsupported-operation-present";

export interface D3NetworkState {
  readonly attempted: false;
  readonly reason: string;
}

export interface D3CommitPayloadRefusal {
  readonly contractVersion: typeof D3_COMMIT_PAYLOAD_VERSION;
  readonly status: "commit-payload-refused";
  readonly authority: "none";
  readonly transmitted: false;
  readonly reason: D3CommitPayloadRefusalReason;
  readonly refusedProposalKinds: readonly D3ProposalKind[];
  readonly network: D3NetworkState;
}

export interface D3CommitPayload {
  readonly contractVersion: typeof D3_COMMIT_PAYLOAD_VERSION;
  readonly status: "commit-payload" | "commit-payload-empty";
  readonly authority: "none";
  readonly transmitted: false;
  readonly idempotencyKey: string;
  readonly baseRevision: D3BaseRevision;
  readonly source: D3CommitPayloadSource;
  readonly changeSummary: D3ChangeSummary;
  readonly manifest: D3CommitManifest;
  readonly manifestJson: string;
  readonly manifestSha256: string;
  readonly manifestByteLength: number;
  readonly payloadJson: string;
  readonly payloadSha256: string;
  readonly payloadByteLength: number;
  readonly network: D3NetworkState;
}

export type D3CommitPayloadResult = D3CommitPayload | D3CommitPayloadRefusal;

export const D3_NETWORK_PENDING_REASON =
  "Awaiting BIM-Hub Identity-token auth contract (see bim-hub-integration/reports/2026-08-11-d3-writeback-go-request.md).";

const SOURCE_DIGEST = /^[0-9a-f]{64}$/;

function assertSource(source: D3CommitPayloadSource): void {
  if (source.datasetId.trim() === "") throw new Error("Commit payload source requires a non-empty dataset id.");
  if (!Number.isSafeInteger(source.byteLength) || source.byteLength < 0) {
    throw new Error("Commit payload source byteLength must be a non-negative safe integer.");
  }
  if (!SOURCE_DIGEST.test(source.sha256)) throw new Error("Commit payload source sha256 must be a 64-character lowercase hex digest.");
}

function assertBaseRevision(baseRevision: D3BaseRevision): void {
  if (!baseRevision.known) return;
  if (baseRevision.projectId.trim() === "" || baseRevision.modelId.trim() === "") {
    throw new Error("A known base revision requires a non-empty projectId and modelId.");
  }
  if (!Number.isSafeInteger(baseRevision.revision) || baseRevision.revision <= 0) {
    throw new Error("A known base revision must be a positive integer.");
  }
}

function assertProposalsMatchSource(source: D3CommitPayloadSource, proposals: readonly D3ProposalEntryInput[]): void {
  const seenKinds = new Set<D3ProposalKind>();
  for (const entry of proposals) {
    if (seenKinds.has(entry.kind)) throw new Error(`Commit payload received the "${entry.kind}" proposal kind more than once.`);
    seenKinds.add(entry.kind);
    const proposalSource = entry.proposal.source;
    if (
      proposalSource.datasetId !== source.datasetId ||
      proposalSource.byteLength !== source.byteLength ||
      proposalSource.sha256 !== source.sha256
    ) {
      throw new Error(
        `The "${entry.kind}" proposal is bound to a different source than the pinned commit payload source receipt.`
      );
    }
  }
}

function changeKind(entry: D3ProposalEntryInput, operationIndex: number): D3ChangeKind {
  if (entry.kind === "local-classification") {
    const operation = entry.proposal.operations[operationIndex];
    return operation.before === null ? "added" : "modified";
  }
  const operation = entry.proposal.operations[operationIndex];
  if (operation.kind === "property-add") return "added";
  if (operation.kind === "property-remove") return "removed";
  return "modified";
}

function sortedByKind(proposals: readonly D3ProposalEntryInput[]): readonly D3ProposalEntryInput[] {
  return [...proposals].sort((left, right) => left.kind.localeCompare(right.kind));
}

/**
 * Builds the deterministic commit payload for a set of already-authored D2-C local proposals, or
 * refuses to build one at all when the set is not entirely writer-supported and unrefused.
 *
 * All-or-nothing gate (mirrors the D2-C exactly-once accounting pattern, and BIM-Hub's own
 * all-or-nothing CAS commit): if *any* supplied proposal was refused as a whole population, or if
 * *any* individual operation across the whole set is `unsupported-current-provider`, no payload is
 * built at all — the function returns a typed refusal instead of a partial payload. This is why a
 * bound local-classification proposal with at least one proposed target currently always refuses
 * the commit payload: every classification operation is `unsupported-current-provider` today.
 */
export async function buildD3CommitPayload(input: D3CommitPayloadInput): Promise<D3CommitPayloadResult> {
  assertSource(input.source);
  assertBaseRevision(input.baseRevision);
  assertProposalsMatchSource(input.source, input.proposals);

  const populationRefused = input.proposals
    .filter((entry) => entry.proposal.status === "local-refusal")
    .map((entry) => entry.kind)
    .sort();
  if (populationRefused.length > 0) {
    return {
      contractVersion: D3_COMMIT_PAYLOAD_VERSION,
      status: "commit-payload-refused",
      authority: "none",
      transmitted: false,
      reason: "proposal-population-refused",
      refusedProposalKinds: populationRefused,
      network: { attempted: false, reason: D3_NETWORK_PENDING_REASON }
    };
  }

  const writerUnsupported = input.proposals
    .filter((entry) => entry.proposal.operations.some((operation) => operation.writerAdmission === "unsupported-current-provider"))
    .map((entry) => entry.kind)
    .sort();
  if (writerUnsupported.length > 0) {
    return {
      contractVersion: D3_COMMIT_PAYLOAD_VERSION,
      status: "commit-payload-refused",
      authority: "none",
      transmitted: false,
      reason: "writer-unsupported-operation-present",
      refusedProposalKinds: writerUnsupported,
      network: { attempted: false, reason: D3_NETWORK_PENDING_REASON }
    };
  }

  const ordered = sortedByKind(input.proposals);
  const changeSummary: { addedCount: number; modifiedCount: number; removedCount: number; totalOperations: number } = {
    addedCount: 0,
    modifiedCount: 0,
    removedCount: 0,
    totalOperations: 0
  };
  const manifestProposals: D3ManifestProposalSummary[] = ordered.map((entry) => {
    entry.proposal.operations.forEach((_operation, operationIndex) => {
      const kind = changeKind(entry, operationIndex);
      if (kind === "added") changeSummary.addedCount += 1;
      else if (kind === "removed") changeSummary.removedCount += 1;
      else changeSummary.modifiedCount += 1;
      changeSummary.totalOperations += 1;
    });
    return {
      kind: entry.kind,
      contractVersion: entry.proposal.contractVersion,
      status: entry.proposal.status as "local-empty" | "local-proposal",
      selected: entry.proposal.selectedPopulation.selected,
      proposed: entry.proposal.selectedPopulation.proposed,
      operationCount: entry.proposal.operations.length,
      operationDigests: entry.proposal.operations.map((operation) => operation.opId)
    };
  });

  const manifest: D3CommitManifest = {
    contractVersion: D3_COMMIT_PAYLOAD_VERSION,
    source: input.source,
    baseRevision: input.baseRevision,
    proposalCount: ordered.length,
    changeSummary,
    proposals: manifestProposals
  };
  const manifestJson = canonicalJson(manifest);
  const manifestBytes = canonicalJsonBytes(manifest);
  const manifestSha256 = await sha256Hex(manifestBytes);

  const payloadEnvelope = {
    contractVersion: D3_COMMIT_PAYLOAD_VERSION,
    source: input.source,
    baseRevision: input.baseRevision,
    proposals: ordered
  };
  const payloadJson = canonicalJson(payloadEnvelope);
  const payloadBytes = canonicalJsonBytes(payloadEnvelope);
  const payloadSha256 = await sha256Hex(payloadBytes);

  const idempotencyKey = `idem-sha256-${await sha256Hex(
    canonicalJsonBytes({ source: input.source, baseRevision: input.baseRevision, manifestSha256, payloadSha256 })
  )}`;

  return {
    contractVersion: D3_COMMIT_PAYLOAD_VERSION,
    status: changeSummary.totalOperations === 0 ? "commit-payload-empty" : "commit-payload",
    authority: "none",
    transmitted: false,
    idempotencyKey,
    baseRevision: input.baseRevision,
    source: input.source,
    changeSummary,
    manifest,
    manifestJson,
    manifestSha256,
    manifestByteLength: manifestBytes.length,
    payloadJson,
    payloadSha256,
    payloadByteLength: payloadBytes.length,
    network: { attempted: false, reason: D3_NETWORK_PENDING_REASON }
  };
}
